/**
 * t657u/t658u: "Sam is typing…" with three small bouncing dots, above a composer (the conversation, or one thread).
 * Calm and compact: one muted line that keeps its height so the composer never jumps; the dots stay still for
 * reduced motion. The names come from the typing store (RealtimeBridge), which drops a typer after 6 s or as soon as
 * their message arrives.
 */
import clsx from 'clsx';
import { typersFor, typingKey, typingLine } from '@tradeoutlet/shared';
import { useAuth } from '@/lib/auth';
import { useMembers } from '@/lib/hooks';
import { useUi } from '@/lib/store';

export function useTypingNames(conversationId: string, parentId?: string | null) {
  const typing = useUi(s => s.typing);
  const { userId } = useAuth();
  const { byId } = useMembers();
  return typersFor(typing, typingKey(conversationId, parentId), userId).map(id => byId.get(id)?.display_name ?? 'Someone');
}

export function TypingDots({ className }: { className?: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-[3px]', className)} aria-hidden data-testid="typing-dots">
      {[0, 1, 2].map(i => (
        <span key={i} className="typing-dot h-1 w-1 rounded-full bg-slate-400 dark:bg-slate-500" style={{ animationDelay: `${i * 160}ms` }} />
      ))}
    </span>
  );
}

export function TypingLine({ conversationId, parentId, className }: { conversationId: string; parentId?: string | null; className?: string }) {
  const line = typingLine(useTypingNames(conversationId, parentId));
  return (
    <div className={clsx('flex h-5 items-center gap-1.5 text-xs text-slate-500', !line && 'invisible', className)} aria-live="polite" data-testid="typing-line">
      {line && <><TypingDots /><span className="truncate">{line}</span></>}
    </div>
  );
}
